// Cache for storing device info
let cachedDeviceInfo = null;
let fetchPromise = null;

export const getDeviceInfo = async () => {
    // Return cached data if available
    if (cachedDeviceInfo) {
        return cachedDeviceInfo;
    }

    // Return existing promise if already fetching
    if (fetchPromise) {
        return fetchPromise;
    }

    // Create new fetch promise
    fetchPromise = (async () => {
        try {
            const userAgent = navigator.userAgent;

            // Detect Device Type
            const isMobile = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
            const isTablet = /iPad|Android(?!.*Mobile)/i.test(userAgent);
            const device = isTablet ? 'Tablet' : isMobile ? 'Mobile' : 'Desktop';

            // Detect OS
            let os = 'Unknown';
            if (/Windows/i.test(userAgent)) os = 'Windows';
            else if (/Mac OS X/i.test(userAgent)) os = 'MacOS';
            else if (/Android/i.test(userAgent)) os = 'Android';
            else if (/iPhone|iPad|iPod/i.test(userAgent)) os = 'iOS';
            else if (/Linux/i.test(userAgent)) os = 'Linux';

            // Detect Browser
            let browser = 'Unknown';
            if (/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent)) browser = 'Chrome';
            else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) browser = 'Safari';
            else if (/Firefox/i.test(userAgent)) browser = 'Firefox';
            else if (/Edg/i.test(userAgent)) browser = 'Edge';
            else if (/Opera|OPR/i.test(userAgent)) browser = 'Opera';

            // Get IP and Location Info
            let ipData = null;
            let locationData = null;

            try {
                // Get IP address
                const ipResponse = await fetch('https://api.ipify.org?format=json');
                ipData = await ipResponse.json();

                // Get location details from IP
                const locationResponse = await fetch(`https://ipapi.co/${ipData.ip}/json/`);
                locationData = await locationResponse.json();
            } catch (error) {
                console.error('Error fetching IP/Location:', error);
            }

            // Compile all information
            const deviceInfo = {
                device: device,
                os: os,
                browser: browser,
                userAgent: userAgent,
                ipAddress: ipData?.ip || 'Unknown',
                location: locationData ? {
                    city: locationData.city,
                    region: locationData.region,
                    country: locationData.country_name,
                    countryCode: locationData.country_code,
                    latitude: locationData.latitude,
                    longitude: locationData.longitude,
                    postal: locationData.postal,
                    timezone: locationData.timezone,
                    isp: locationData.org
                } : null,
                screen: {
                    width: window.screen.width,
                    height: window.screen.height,
                    availWidth: window.screen.availWidth,
                    availHeight: window.screen.availHeight,
                    colorDepth: window.screen.colorDepth,
                    pixelRatio: window.devicePixelRatio || 1
                },
                viewport: {
                    width: window.innerWidth,
                    height: window.innerHeight
                },
                language: navigator.language,
                languages: navigator.languages,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                cookieEnabled: navigator.cookieEnabled,
                doNotTrack: navigator.doNotTrack,
                timestamp: new Date().toISOString()
            };

            // Cache the result
            cachedDeviceInfo = deviceInfo;
            fetchPromise = null;

            return deviceInfo;
        } catch (error) {
            fetchPromise = null;
            console.error('Error getting device info:', error);
            throw error;
        }
    })();

    return fetchPromise;
};

/**
 * Clear cached device info (useful for testing or forcing refresh)
 */
export const clearDeviceInfoCache = () => {
    cachedDeviceInfo = null;
    fetchPromise = null;
};

/**
 * Gets precise GPS location (requires user permission)
 * @returns {Promise<Object>} GPS coordinates
 */
export const getGPSLocation = () => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocation not supported'));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    altitude: position.coords.altitude,
                    heading: position.coords.heading,
                    speed: position.coords.speed,
                    timestamp: position.timestamp
                });
            },
            (error) => {
                reject(error);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );
    });
};

/**
 * Simplified version - just basic info
 * @returns {Object} Basic device information (synchronous)
 */
export const getBasicDeviceInfo = () => {
    const userAgent = navigator.userAgent;

    const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(userAgent);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(userAgent);

    return {
        device: isTablet ? 'Tablet' : isMobile ? 'Mobile' : 'Desktop',
        isMobile,
        isTablet,
        isDesktop: !isMobile && !isTablet,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
};